import { Badge, HStack, Skeleton, Text, Tooltip } from "@chakra-ui/react";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  DataTable,
  Link,
  ModalConfirm,
  UserTooltip,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  ProjectOrderByColumnEnum,
  ProjectTypeEnum,
} from "@src/__generated__/urql-graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { ExternalAccessIcon } from "@src/components/widgets/ExternalAccessIcon";
import {
  LongContentCell,
  ProjectCell,
} from "@src/components/widgets/TableCell";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { currency, percent } from "@src/utils/formatters";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { type FC, useMemo } from "react";
import { BUDGET_ITEM_HASH_INDEX_SEPARATOR } from "../../detail/BudgetItems/ItemTitle";
import { type Store, useStore } from "../Store";
import Column from "../Table/column";

type TSubRow = {
  id: string;
  title: string;
  categoryName: string;
  stats: {
    net_profit: number;
    net_margin_percent: number;
    invoiced_percent: number;
    efficiency_percent?: number;
    remaining_budget?: number;
  };
};

type TFinishedProject = Store["finishedProjects"][0];

type FinishedProjectTableRow =
  | {
      id: string;
      rowType: "project";
      project: TFinishedProject;
      __subRows?: FinishedProjectTableRow[];
    }
  | {
      id: string;
      rowType: "subRow";
      project: TFinishedProject;
      subRow: TSubRow;
    };

const isProjectRow = (
  row: FinishedProjectTableRow,
): row is Extract<FinishedProjectTableRow, { rowType: "project" }> =>
  row.rowType === "project";

const TableBody: FC<{ fetching?: boolean }> = ({ fetching }) => {
  const store = useStore();

  const data = useMemo<FinishedProjectTableRow[]>(() => {
    return store.finishedProjects.map((project) => {
      const subRows: TSubRow[] = [];
      const ourWorkCount = project?.ourWork?.length ?? 0;
      const expenseCount = project?.expenses?.length ?? 0;

      for (let i = 0; i < ourWorkCount; i++) {
        const item = project?.ourWork?.[i];
        if (!item) continue;
        subRows.push({
          id: item.id,
          categoryName: item.category.name,
          title: item.title,
          stats: item.stats,
        });
      }

      for (let i = 0; i < expenseCount; i++) {
        const item = project?.expenses?.[i];
        if (!item) continue;
        subRows.push({
          id: item.id,
          title: item.title,
          categoryName: item.category.name,
          stats: {
            invoiced_percent: item.stats.invoiced_percent,
            net_profit: item.stats.profit,
            net_margin_percent: item.stats.profit_percent,
          },
        });
      }

      if (!project?.expenses) {
        subRows.push({
          id: "",
          title: "",
          stats: {
            net_profit: 0,
            invoiced_percent: 0,
            net_margin_percent: 0,
          },
          categoryName: "",
        });
      }

      const normalizedSubRows: FinishedProjectTableRow[] = subRows.map(
        (subRow) => ({
          id: subRow.id,
          rowType: "subRow",
          project,
          subRow,
        }),
      );

      return {
        id: project.id,
        rowType: "project",
        project,
        __subRows: normalizedSubRows.length > 0 ? normalizedSubRows : undefined,
      };
    });
  }, [store.finishedProjects]);

  const columns = useMemo<
    BodyColumnDef<FinishedProjectTableRow, ProjectOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "projectManager",
        size: 35,
        meta: { label: t`PM` },
        header: t`PM`,
        orderBy: ProjectOrderByColumnEnum.ProjectManager,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const projectManager = row.original.project.projectManager;

          return (
            <UserTooltip user={projectManager}>
              <Avatar
                name={projectManager?.full_name}
                src={projectManager?.image?.urls.thumbnail}
              />
            </UserTooltip>
          );
        },
      },
      {
        id: "title",
        meta: { label: t`Project` },
        header: t`Project`,
        orderBy: ProjectOrderByColumnEnum.ProjectCode,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const project = row.original.project;
            return (
              <ProjectCell.Title
                project={project}
                brand={project.brand}
                billable={project.billable}
              />
            );
          }

          const { project, subRow } = row.original;
          const label = `${subRow.title} - ${subRow.categoryName}`;

          return (
            <LongContentCell maxWidth={180} tooltip={label}>
              <Link
                href={{
                  pathname: "/projects/detail",
                  query: { id: project.id },
                  hash: kebabCase(
                    `${subRow.title}${BUDGET_ITEM_HASH_INDEX_SEPARATOR}0`,
                  ),
                }}
              >
                {label}
              </Link>
            </LongContentCell>
          );
        },
      },
      {
        id: "project_profit",
        meta: { label: t`Budget Profit` },
        header: t`Budget Profit`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const { statsCached, type } = row.original.project;

          return (
            <Skeleton h="3" isLoaded={!fetching}>
              <WrapComponent
                if={type === ProjectTypeEnum.TimeMaterials}
                with={(children) => (
                  <HStack>
                    <Tooltip label={t`Time & Materials`} shouldWrapChildren>
                      <Icon name="time-and-materials" />
                    </Tooltip>
                    {children}
                  </HStack>
                )}
              >
                <Text>
                  {currency.formatByWorkspace(statsCached?.profit ?? 0)}
                </Text>
              </WrapComponent>
            </Skeleton>
          );
        },
      },
      {
        id: "revenue",
        meta: { label: t`Revenue` },
        header: t`Revenue`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const { statsCached } = row.original.project;

          return (
            <Skeleton h="3" isLoaded={!fetching}>
              {currency.formatByWorkspace(
                (statsCached?.credit ?? 0) +
                  (statsCached?.unused_sum_from_proformas ?? 0),
              )}
            </Skeleton>
          );
        },
      },
      {
        id: "net_profit",
        meta: { label: t`Net Profit` },
        header: t`Net Profit`,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            return (
              <Skeleton h="3" isLoaded={!fetching}>
                <Text>
                  {currency.formatByWorkspace(
                    row.original.project.statsCached?.net_profit ?? 0,
                  )}
                </Text>
              </Skeleton>
            );
          }

          return (
            <Text>
              {currency.formatByWorkspace(row.original.subRow.stats.net_profit)}
            </Text>
          );
        },
      },
      {
        id: "net_margin",
        meta: { label: t`Net Margin` },
        header: t`Net Margin`,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const netMarginPercent =
              row.original.project.statsCached?.net_margin_percent ?? 0;

            return (
              <Skeleton h="3" isLoaded={!fetching}>
                <Text color={netMarginPercent > 0 ? "green.500" : "red.500"}>
                  {percent.format(netMarginPercent)}
                </Text>
              </Skeleton>
            );
          }

          return (
            <Text
              color={
                row.original.subRow.stats.net_margin_percent > 0
                  ? "green.500"
                  : "red.500"
              }
            >
              {percent.format(row.original.subRow.stats.net_margin_percent)}
            </Text>
          );
        },
      },
      {
        id: "work_efficiency",
        meta: { label: t`Work efficiency` },
        header: t`Work efficiency`,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            const efficiencyPercent =
              row.original.project.statsCached?.ourWork.efficiency_percent ?? 0;
            const remainingBudget =
              row.original.project.statsCached?.ourWork.remaining_budget ?? 0;

            return (
              <Skeleton h="3" isLoaded={!fetching}>
                <Text color={efficiencyPercent < 10000 ? "red.500" : undefined}>
                  {percent.format(efficiencyPercent)} (
                  {currency.formatByWorkspace(remainingBudget)})
                </Text>
              </Skeleton>
            );
          }

          const { efficiency_percent, remaining_budget } =
            row.original.subRow.stats;

          if (!efficiency_percent || !remaining_budget) {
            return null;
          }

          return (
            <Text color={efficiency_percent < 10000 ? "red.500" : undefined}>
              {percent.format(efficiency_percent)} (
              {currency.formatByWorkspace(remaining_budget)})
            </Text>
          );
        },
      },
      {
        id: "invoicedPercentage",
        meta: { label: t`Invoiced` },
        header: t`Invoiced`,
        cell: ({ row }) => {
          if (isProjectRow(row.original)) {
            return (
              <Column.Invoiced
                searchParam={row.original.project.code}
                percent={
                  +(row.original.project.statsCached?.invoiced_percent ?? 0)
                }
              />
            );
          }

          return (
            <Column.Invoiced
              searchParam={row.original.subRow.title}
              percent={+row.original.subRow.stats.invoiced_percent}
            />
          );
        },
      },
      {
        id: "project-categories",
        meta: { label: t`Category` },
        header: t`Category`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          return (
            <Skeleton
              minH="4"
              isLoaded={Array.isArray(row.original.project.categories)}
            >
              <HStack wrap="wrap" mt="1" whiteSpace="normal">
                {row.original.project.categories?.map(({ id, name }) => (
                  <Badge
                    key={`${id}-${name}`}
                    fontSize="2xs"
                    fontWeight="thin"
                    bg="grey.50"
                    size="xs"
                  >
                    {name}
                  </Badge>
                ))}
              </HStack>
            </Skeleton>
          );
        },
      },
      {
        id: "visible",
        size: 35,
        enableResizing: false,
        enableHiding: false,
        meta: { label: "", isReorderDisabled: true },
        header: "",
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const project = row.original.project;

          return (
            <ExternalAccessIcon
              key={project.scopes?.length?.toString()}
              type="project"
              id={project.id}
              scopes={project.scopes ?? []}
              isTemplate={project.is_template ?? false}
            />
          );
        },
      },
      {
        id: "note",
        size: 61,
        meta: { label: t`Note` },
        header: t`Note`,
        cell: ({ row }) =>
          isProjectRow(row.original) ? (
            <Column.Note
              note={row.original.project.note ?? undefined}
              id={row.original.project.id}
            />
          ) : null,
      },
      {
        id: ACTIONS_COLUMN_ID,
        enableResizing: false,
        meta: { label: t`Actions`, isReorderDisabled: true },
        header: t`Actions`,
        cell: ({ row }) => {
          if (!isProjectRow(row.original)) {
            return null;
          }

          const project = row.original.project;

          if (!project.id || !project.projectManager || fetching) {
            return null;
          }

          return (
            <Column.Actions
              project={{
                ...project,
                billable: project.billable ?? false,
                isTemplate: project.is_template ?? false,
                is_starred: project.is_starred ?? false,
              }}
            />
          );
        },
      },
    ],
    [fetching],
  );

  return (
    <>
      <DataTable.Body
        tableKey="finished-projects"
        data={data}
        columns={columns}
        orderedBy={store.orderBy.clauses[0]}
        onOrderByChange={({ column, order }) => {
          if (!order) {
            store.orderBy.setClauses([]);
            store.pagination.resetPage();
            return;
          }

          store.orderBy.setClauses([
            {
              column,
              order,
            },
          ]);
          store.pagination.resetPage();
        }}
        selected={store.selectedProjectsIds}
        onSelect={store.setSelectedProjectIds}
        allIds={store.finishedProjectsSummary?.projectIds}
        bulkActions={store.bulkActionsStore.bulkActions}
        enableSubRowSelection={false}
      />
      <Pagination store={store.pagination} />
      <ModalConfirm
        isOpen={store.bulkActionsStore.confirmDialog.isOpen}
        onClose={store.bulkActionsStore.confirmDialog.onClose}
        onConfirm={store.bulkActionsStore.confirm}
        isLoading={store.bulkActionsStore.isConfirmDialogLoading}
      />
    </>
  );
};

export default observer(TableBody);
